import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Self,
} from '@angular/core';
import { ControlValueAccessor, NgControl } from '@angular/forms';
import uniqueId from 'lodash/uniqueId';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { FormFieldControl } from '../form-field/form-field-control';
import { FormService } from '../form.service';
import { ControlWidth } from '../forms.interfaces';

/**
 * Supported input types.
 *
 * Note: Some HTML standard types are not supported here because they have their own component.
 */
export type InputType =
  | 'color'
  | 'date'
  | 'email'
  | 'file'
  | 'image'
  | 'number'
  | 'password'
  | 'range'
  | 'tel'
  | 'text'
  | 'textarea'
  | 'time';

@Component({
  selector: 'zc-input',
  templateUrl: './input.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: FormFieldControl,
      useExisting: InputComponent,
    },
  ],
})
export class InputComponent
  implements
    ControlValueAccessor,
    FormFieldControl<any>,
    OnInit,
    OnChanges,
    OnDestroy
{
  constructor(
    @Self() public ngControl: NgControl,
    private form: FormService,
    private ref: ChangeDetectorRef
  ) {
    ngControl.valueAccessor = this;
  }

  get value() {
    return this._value;
  }

  set value(value) {
    this._value = value;
    this.onChange(value);
    this.stateChanges.next();
  }

  @Input() disabled = false;
  @Input() id = uniqueId('control-');
  @Input() placeholder = '';
  @Input() readonly = false;
  @Input() type = 'text';
  @Input() width: ControlWidth = 'md';
  @Input() hasMask = false;
  @Input() maskPattern = '';
  required: boolean;
  readonly stateChanges = new Subject<void>();

  private _value: any;
  private readonly _destroyed = new Subject<void>();

  ngOnInit() {
    this.setRequired();
    this.ngControl.statusChanges
      .pipe(
        takeUntil(this._destroyed),
        tap(() => this.setRequired())
      )
      .subscribe();
  }

  ngOnChanges() {
    this.stateChanges.next();
  }

  ngOnDestroy() {
    this._destroyed.next();
    this.stateChanges.complete();
  }

  onFocus() {
    // Don't want the field to be considered
    // touched/changed/dirty if it's readonly
    if (this.readonly) {
      return;
    }
  }

  onBlur() {
    // Don't want the field to be considered
    // touched/changed/dirty if it's readonly
    if (this.readonly) {
      return;
    }
    this.onTouched();
    this.stateChanges.next();
  }

  onChange = (_: any) => {
    // empty
  };

  onTouched = () => {
    // empty
  };

  writeValue(value: any) {
    this.value = value;
    this.ref.markForCheck();
  }

  registerOnChange(fn: (_: any) => void) {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void) {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean) {
    this.disabled = isDisabled;
    this.ref.markForCheck();
  }

  private setRequired() {
    const ctrl = this.ngControl.control;

    this.required = this.form.hasRequired(ctrl);
    this.stateChanges.next();
  }
}
